if (!customElements.get('wholesale-product')) {
  customElements.define(
    'wholesale-product',
    class WholesaleProduct extends HTMLElement {
      constructor() {
        super();

        this.variantData = this.parseVariantData();
        this.activeVariantId = Number(this.dataset.activeVariantId) || this.variantData[0]?.id || null;
        this.quantityInputs = Array.from(this.querySelectorAll('input[data-variant-id]'));
        this.addButton = this.querySelector('[data-add-all]');
        this.guestModalButton = this.querySelector('[data-open-guest-modal]');
        this.clearButton = this.querySelector('[data-clear-quantities]');
        this.messageNode = this.querySelector('[data-bulk-message]');
        this.priceNode = this.querySelector('[data-active-price]');
        this.discountNode = this.querySelector('[data-active-discount]');
        this.skuNode = this.querySelector('[data-active-sku]');
        this.totalPairsNodes = this.querySelectorAll('[data-total-pairs]');
        this.totalMoneyNodes = this.querySelectorAll('[data-total-money]');
        this.selectedSizesNodes = this.querySelectorAll('[data-selected-sizes]');
        this.rowSubtotalNodes = Array.from(this.querySelectorAll('[data-row-subtotal]'));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.cartQuantities = new Map();
        this.cartStatePromise = null;
        this.sizeGuideModal = this.querySelector('[data-size-guide-modal]');
        this.openSizeGuideButton = this.querySelector('[data-size-guide-open]');
        this.boundOnSizeGuideKeydown = this.onSizeGuideKeydown.bind(this);
        this.lastTotalMoneyText = '';
        this.isUpdatingSummary = false;
        this.rowSubtotalRefreshQueued = false;
      }

      connectedCallback() {
        this.quantityInputs.forEach((input) => {
          input.dataset.previousValue = input.value || '0';
          input.addEventListener('change', this.onQuantityChange.bind(this));
          input.addEventListener('input', this.onQuantityChange.bind(this));
          input.addEventListener('focus', this.onVariantFocus.bind(this));
        });

        this.querySelectorAll('[data-variant-activate]').forEach((button) => {
          button.addEventListener('click', this.onVariantActivate.bind(this));
        });

        this.addButton?.addEventListener('click', this.onAddAll.bind(this));
        this.guestModalButton?.addEventListener('click', this.onGuestModalOpen.bind(this));
        this.clearButton?.addEventListener('click', this.onClearAll.bind(this));
        this.openSizeGuideButton?.addEventListener('click', this.openSizeGuide.bind(this));
        this.querySelectorAll('[data-size-guide-close]').forEach((button) => {
          button.addEventListener('click', this.closeSizeGuide.bind(this));
        });

        this.initMobileGalleryDots();
        this.startSummaryTotalsObserver();
        this.startRowSubtotalObserver();
        this.updateState();
        this.updateActiveVariant(this.activeVariantId);
        this.syncCartState({ silent: true }).then(() => {
          this.refreshInputConstraints();
          this.updateState();
        });
      }

      disconnectedCallback() {
        this.summaryTotalsObserver?.disconnect();
        this.rowSubtotalObserver?.disconnect();
        document.removeEventListener('keydown', this.boundOnSizeGuideKeydown);
      }

      parseVariantData() {
        try {
          return JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]');
        } catch (error) {
          console.error('Unable to parse wholesale variant data', error);
          return [];
        }
      }

      openSizeGuide() {
        if (!this.sizeGuideModal) return;
        this.sizeGuideModal.hidden = false;
        this.loadSizeGuideContent();
        document.documentElement.classList.add('gt-pdp-size-guide-open');
        document.addEventListener('keydown', this.boundOnSizeGuideKeydown);
        this.sizeGuideModal.querySelector('[data-size-guide-close]')?.focus();
      }

      closeSizeGuide() {
        if (!this.sizeGuideModal) return;
        this.sizeGuideModal.hidden = true;
        document.documentElement.classList.remove('gt-pdp-size-guide-open');
        document.removeEventListener('keydown', this.boundOnSizeGuideKeydown);
        this.openSizeGuideButton?.focus();
      }

      onSizeGuideKeydown(event) {
        if (event.key === 'Escape') this.closeSizeGuide();
      }

      async loadSizeGuideContent() {
        if (!this.sizeGuideModal || this.sizeGuideModal.dataset.sizeGuideLoaded === 'true') return;

        const contentNode = this.sizeGuideModal.querySelector('[data-size-guide-content]');
        if (!contentNode) return;

        const guideUrl = this.sizeGuideModal.dataset.sizeGuideUrl;
        if (!guideUrl) {
          this.sizeGuideModal.dataset.sizeGuideLoaded = 'true';
          return;
        }

        contentNode.innerHTML = '<p class="gt-pdp__size-guide-loading">Cargando guia de tallas...</p>';

        try {
          const response = await fetch(guideUrl, {
            headers: {
              Accept: 'text/html',
              'X-Requested-With': 'XMLHttpRequest',
            },
          });

          if (!response.ok) throw new Error(`Size guide request failed with status ${response.status}`);

          const html = await response.text();
          const documentHtml = new DOMParser().parseFromString(html, 'text/html');
          const source =
            documentHtml.querySelector('#MainContent .shopify-section .page-width') ||
            documentHtml.querySelector('#MainContent .page-width') ||
            documentHtml.querySelector('#MainContent') ||
            documentHtml.querySelector('main');

          if (!source) throw new Error('Size guide content not found');

          const content = source.cloneNode(true);
          content.querySelectorAll('script, style, noscript, header, footer').forEach((node) => node.remove());
          contentNode.innerHTML = content.innerHTML.trim() || this.getSizeGuideFallbackHtml();
          this.sizeGuideModal.dataset.sizeGuideLoaded = 'true';
        } catch (error) {
          console.error('Unable to load size guide content', error);
          contentNode.innerHTML = this.getSizeGuideFallbackHtml();
          this.sizeGuideModal.dataset.sizeGuideLoaded = 'true';
        }
      }

      getSizeGuideFallbackHtml() {
        return this.sizeGuideModal?.querySelector('[data-size-guide-fallback]')?.innerHTML || '';
      }

      initMobileGalleryDots() {
        const galleryViewer = this.querySelector('[id^="GalleryViewer"]');
        const slider = galleryViewer?.querySelector('[id^="Slider-Gallery"]');
        const counter = galleryViewer?.querySelector('.slider-counter');
        const slides = Array.from(slider?.querySelectorAll('[id^="Slide-"]') || []);

        if (!galleryViewer || !slider || !counter || slides.length < 2 || counter.querySelector('.gt-pdp__mobile-gallery-dot')) return;

        counter.classList.add('gt-pdp__mobile-gallery-dots');
        counter.innerHTML = '';

        const dots = slides.map((slide, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'gt-pdp__mobile-gallery-dot';
          button.setAttribute('aria-label', `Ver imagen ${index + 1}`);
          button.addEventListener('click', () => {
            slider.scrollTo({
              left: slide.offsetLeft,
              behavior: 'smooth',
            });
          });
          counter.appendChild(button);
          return button;
        });

        const updateDots = () => {
          const currentIndex = slides.reduce(
            (closest, slide, index) => {
              const distance = Math.abs(slide.offsetLeft - slider.scrollLeft);
              return distance < closest.distance ? { index, distance } : closest;
            },
            { index: 0, distance: Number.POSITIVE_INFINITY }
          ).index;

          dots.forEach((dot, index) => {
            const isActive = index === currentIndex;
            dot.classList.toggle('is-active', isActive);
            dot.toggleAttribute('aria-current', isActive);
          });
        };

        slider.addEventListener('scroll', updateDots, { passive: true });
        galleryViewer.addEventListener('slideChanged', updateDots);
        requestAnimationFrame(updateDots);
      }

      getVariantById(id) {
        return this.variantData.find((variant) => variant.id === Number(id));
      }

      onGuestModalOpen(event) {
        event.preventDefault();
        const modal = document.getElementById('gt-guest-modal');
        if (modal) {
          modal.removeAttribute('hidden');
          modal.querySelector('.gt-guest-modal__close, a, button')?.focus();
          return;
        }

        const loginDrawer = document.querySelector('login-drawer');
        if (loginDrawer && typeof loginDrawer.open === 'function') {
          loginDrawer.open(event.currentTarget);
          return;
        }

        window.location.href = '/pages/registro-mayorista';
      }

      readNumber(...values) {
        for (const value of values) {
          if (value === '' || value === null || value === undefined) continue;
          const numericValue = Number(value);
          if (Number.isFinite(numericValue)) return numericValue;
        }

        return null;
      }

      getInputForVariant(variantId) {
        return this.querySelector(`input[data-variant-id="${variantId}"]`);
      }

      getCartQuantityForVariant(variantId) {
        return Number(this.cartQuantities.get(Number(variantId)) || 0);
      }

      getLowStockThreshold() {
        return Math.max(1, Number(this.dataset.lowStockThreshold || 8));
      }

      getInventoryLabels() {
        return {
          inStock: this.dataset.inStockLabel || 'Disponible',
          lowStock: this.dataset.lowStockLabel || 'Stock corto',
          backorder: this.dataset.backorderLabel || 'Sobre pedido',
          soldOut: this.dataset.soldOutLabel || 'Agotado',
          inCart: this.dataset.inCartLabel || 'En carrito',
        };
      }

      getVariantConstraints(variant, input = this.getInputForVariant(variant?.id)) {
        const step = Math.max(1, this.readNumber(input?.dataset.step, variant?.step, 1) || 1);
        const minimum = Math.max(0, this.readNumber(input?.dataset.min, variant?.min, 0) || 0);
        const quantityRuleMax = this.readNumber(input?.dataset.max, input?.max, variant?.max);
        const inventoryQuantity = this.readNumber(
          variant?.inventory_quantity,
          variant?.inventoryQuantity,
          input?.dataset.inventoryQuantity
        );
        const inventoryPolicy = `${variant?.inventory_policy || variant?.inventoryPolicy || input?.dataset.inventoryPolicy || ''}`.toLowerCase();
        const inventoryManaged = Boolean(
          variant?.inventory_managed ||
            variant?.inventoryManaged ||
            variant?.inventory_management ||
            variant?.inventoryManagement ||
            input?.dataset.inventoryManaged === 'true'
        );
        const checkAgainstInventory = Boolean(
          variant?.check_against_inventory ||
            variant?.checkAgainstInventory ||
            (inventoryManaged && inventoryPolicy !== 'continue' && Number.isFinite(inventoryQuantity))
        );
        const cartQuantity = this.getCartQuantityForVariant(variant?.id);

        let inventoryCap = null;
        if (checkAgainstInventory && Number.isFinite(inventoryQuantity)) {
          inventoryCap = Math.max(0, inventoryQuantity - cartQuantity);
        }

        let effectiveMax = null;
        if (quantityRuleMax !== null && quantityRuleMax >= 0) effectiveMax = quantityRuleMax;
        if (inventoryCap !== null) {
          effectiveMax = effectiveMax === null ? inventoryCap : Math.min(effectiveMax, inventoryCap);
        }

        return {
          step,
          minimum,
          maximum: effectiveMax,
          quantityRuleMax,
          inventoryQuantity,
          inventoryCap,
          cartQuantity,
          inventoryManaged,
          checkAgainstInventory,
          inventoryPolicy,
        };
      }

      getVariantInventoryState(variant, constraints = this.getVariantConstraints(variant)) {
        const lowStockThreshold = this.getLowStockThreshold();

        if (!variant?.available || constraints.maximum === 0 || (constraints.maximum !== null && constraints.maximum < constraints.minimum)) {
          return 'sold-out';
        }

        if (constraints.inventoryManaged && constraints.inventoryPolicy === 'continue' && Number(constraints.inventoryQuantity || 0) <= 0) {
          return 'backorder';
        }

        if (constraints.checkAgainstInventory && constraints.inventoryCap !== null && constraints.inventoryCap <= lowStockThreshold) {
          return 'low-stock';
        }

        return 'available';
      }

      getNearestAllowedQuantity(quantity, constraints, direction = 'nearest') {
        const { minimum, maximum, step } = constraints;
        let normalizedQuantity = Number(quantity) || 0;

        if (normalizedQuantity <= 0) return 0;
        if (normalizedQuantity < minimum) normalizedQuantity = minimum;

        if (step > 1) {
          if (minimum > 0) {
            const offset = normalizedQuantity - minimum;
            const roundedOffset =
              direction === 'down'
                ? Math.floor(offset / step) * step
                : Math.ceil(offset / step) * step;
            normalizedQuantity = minimum + Math.max(0, roundedOffset);
          } else {
            normalizedQuantity =
              direction === 'down'
                ? Math.floor(normalizedQuantity / step) * step
                : Math.ceil(normalizedQuantity / step) * step;
          }
        }

        if (maximum !== null && normalizedQuantity > maximum) {
          if (maximum <= 0) return 0;

          if (step > 1) {
            if (minimum > 0) {
              const usableMaximum = Math.max(minimum, maximum);
              const offset = usableMaximum - minimum;
              normalizedQuantity = minimum + Math.max(0, Math.floor(offset / step) * step);
            } else {
              normalizedQuantity = Math.max(step, Math.floor(maximum / step) * step);
            }
          } else {
            normalizedQuantity = maximum;
          }
        }

        if (normalizedQuantity > 0 && normalizedQuantity < minimum) return 0;
        if (maximum !== null && normalizedQuantity > maximum) return 0;

        return Math.max(0, normalizedQuantity);
      }

      sanitizeQuantity(input, variant, direction = 'up') {
        const constraints = this.getVariantConstraints(variant, input);
        let quantity = Number(input?.value || 0);

        if (quantity <= 0) return 0;

        quantity = this.getNearestAllowedQuantity(quantity, constraints, direction === 'down' ? 'down' : 'up');

        if (constraints.maximum !== null && constraints.maximum <= 0) return 0;

        return quantity;
      }

      refreshInputConstraints() {
        this.quantityInputs.forEach((input) => {
          const variant = this.getVariantById(input.dataset.variantId);
          if (!variant) return;

          const constraints = this.getVariantConstraints(variant, input);
          const quantity = this.sanitizeQuantity(input, variant, 'down');
          const isUnavailable = variant.available === false || constraints.maximum === 0;

          input.value = quantity;
          input.step = `${constraints.step}`;
          input.dataset.step = `${constraints.step}`;
          input.dataset.min = `${constraints.minimum}`;
          input.min = '0';

          if (constraints.maximum !== null) {
            input.max = `${constraints.maximum}`;
            input.dataset.max = `${constraints.maximum}`;
          } else {
            input.removeAttribute('max');
            input.removeAttribute('data-max');
          }

          input.dataset.previousValue = `${quantity}`;
          input.toggleAttribute('disabled', isUnavailable);
          input
            .closest('quantity-input')
            ?.querySelectorAll('button')
            .forEach((button) => button.toggleAttribute('disabled', isUnavailable));
        });

        this.updateInventoryUI();
      }

      updateInventoryUI() {
        const labels = this.getInventoryLabels();

        this.querySelectorAll('[data-variant-row]').forEach((row) => {
          const variant = this.getVariantById(row.dataset.variantId);
          if (!variant) return;

          const constraints = this.getVariantConstraints(variant);
          const state = this.getVariantInventoryState(variant, constraints);
          const statusNode = row.querySelector('[data-variant-status]');
          const inventoryNoteNode = row.querySelector('[data-inventory-note]');
          const cartNoteNode = row.querySelector('[data-cart-note]');

          row.dataset.inventoryState = state;
          row.classList.toggle('is-disabled', state === 'sold-out');
          row.classList.toggle('is-low-stock', state === 'low-stock');

          if (statusNode) {
            statusNode.className = `gt-pdp__variant-pill gt-pdp__variant-pill--${state}`;
            statusNode.textContent =
              state === 'sold-out'
                ? labels.soldOut
                : state === 'low-stock'
                  ? labels.lowStock
                  : state === 'backorder'
                    ? labels.backorder
                    : labels.inStock;
          }

          if (inventoryNoteNode) {
            if (state === 'backorder') {
              inventoryNoteNode.textContent = labels.backorder;
            } else if (constraints.checkAgainstInventory && constraints.inventoryCap !== null && state !== 'sold-out') {
              inventoryNoteNode.textContent = `${constraints.inventoryCap} disponibles`;
            } else {
              inventoryNoteNode.textContent = '';
            }
          }

          if (cartNoteNode) {
            cartNoteNode.textContent = constraints.cartQuantity > 0 ? `${labels.inCart}: ${constraints.cartQuantity}` : '';
          }
        });
      }

      async syncCartState({ silent = false } = {}) {
        if (this.cartStatePromise) return this.cartStatePromise;

        this.cartStatePromise = fetch(`${routes.cart_url}.js`, {
          headers: {
            Accept: 'application/json',
          },
        })
          .then((response) => {
            if (!response.ok) throw new Error(`Cart request failed with status ${response.status}`);
            return response.json();
          })
          .then((cart) => {
            this.cartQuantities = new Map();
            (cart.items || []).forEach((item) => {
              const variantId = Number(item.variant_id);
              if (!variantId) return;
              this.cartQuantities.set(variantId, Number(item.quantity || 0));
            });

            return cart;
          })
          .catch((error) => {
            if (!silent) {
              console.error('Unable to sync cart state', error);
            }
            return null;
          })
          .finally(() => {
            this.cartStatePromise = null;
          });

        return this.cartStatePromise;
      }

      onVariantActivate(event) {
        const variantId = event.currentTarget.dataset.variantId;
        this.updateActiveVariant(variantId);
      }

      onVariantFocus(event) {
        this.updateActiveVariant(event.target.dataset.variantId);
      }

      onQuantityChange(event) {
        const input = event.target;
        const variant = this.getVariantById(input.dataset.variantId);
        if (!variant) return;

        const previousQuantity = Number(input.dataset.previousValue || 0);
        const nextQuantity = this.sanitizeQuantity(input, variant, Number(input.value || 0) < previousQuantity ? 'down' : 'up');
        input.value = nextQuantity;
        input.dataset.previousValue = `${nextQuantity}`;
        this.updateActiveVariant(variant.id);
        this.updateState();
      }

      getEffectiveUnitPrice(variant, quantity) {
        if (!variant) return 0;
        const priceBreaks = Array.isArray(variant.quantity_price_breaks) ? variant.quantity_price_breaks : [];
        let unitPrice = Number(variant.price || 0);

        if (quantity > 0 && priceBreaks.length) {
          priceBreaks.forEach((priceBreak) => {
            if (quantity >= Number(priceBreak.minimum_quantity)) {
              unitPrice = Number(priceBreak.price);
            }
          });
        }

        return unitPrice;
      }

      getEffectiveCompareUnitPrice(variant, quantity) {
        if (!variant) return 0;

        const finalUnitPrice = this.getEffectiveUnitPrice(variant, quantity);
        let compareUnitPrice = Number(variant.compare_at_price || 0);

        if (!Number.isFinite(compareUnitPrice) || compareUnitPrice <= 0) {
          compareUnitPrice = finalUnitPrice;
        }

        if (compareUnitPrice < finalUnitPrice) {
          compareUnitPrice = finalUnitPrice;
        }

        return compareUnitPrice;
      }

      formatMoney(cents) {
        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
          return window.Shopify.formatMoney(cents, this.dataset.moneyFormat);
        }
        return `$${(cents / 100).toFixed(2)}`;
      }

      getCleanText(element) {
        return `${element?.textContent || ''}`.replace(/\u00a0/g, ' ').trim();
      }

      parseMoneyToCents(value) {
        const text = `${value || ''}`.replace(/\u00a0/g, ' ').trim();
        if (!text) return null;

        const matches = text.match(/-?\d[\d.,]*/g);
        const normalized = matches?.[matches.length - 1]?.replace(/[^\d.,-]/g, '');
        if (!normalized) return null;

        const separatorCount = (normalized.match(/[.,]/g) || []).length;
        const onlySeparator = normalized.includes(',') && !normalized.includes('.') ? ',' : normalized.includes('.') && !normalized.includes(',') ? '.' : null;

        if (separatorCount === 1 && onlySeparator) {
          const separatorIndex = normalized.lastIndexOf(onlySeparator);
          const separatorTail = normalized.slice(separatorIndex + 1);

          if (separatorTail.length > 3) {
            const cents = parseInt(normalized.replace(/[^\d-]/g, ''), 10);
            return Number.isNaN(cents) ? null : cents;
          }

          if (separatorTail.length !== 2) {
            const wholeNumber = parseInt(normalized.replace(/[^\d-]/g, ''), 10);
            return Number.isNaN(wholeNumber) ? null : wholeNumber * 100;
          }
        }

        const lastComma = normalized.lastIndexOf(',');
        const lastDot = normalized.lastIndexOf('.');

        if (Math.max(lastComma, lastDot) === -1) {
          const wholeNumber = parseInt(normalized.replace(/[^\d-]/g, ''), 10);
          return Number.isNaN(wholeNumber) ? null : wholeNumber * 100;
        }

        const integerPart = normalized
          .slice(0, Math.max(lastComma, lastDot))
          .replace(/[^\d-]/g, '');
        const decimalPart = normalized.slice(Math.max(lastComma, lastDot) + 1).replace(/\D/g, '').padEnd(2, '0').slice(0, 2);
        const cents = parseInt(`${integerPart || '0'}${decimalPart}`, 10);
        return Number.isNaN(cents) ? null : cents;
      }

      startSummaryTotalsObserver() {
        if (this.summaryTotalsObserver || !window.MutationObserver) return;

        this.summaryTotalsObserver = new MutationObserver(() => {
          if (this.isUpdatingSummary || !this.lastTotalMoneyText) return;

          this.totalMoneyNodes.forEach((node) => {
            if (this.getCleanText(node) !== this.lastTotalMoneyText) {
              node.textContent = this.lastTotalMoneyText;
            }
          });
        });

        this.totalMoneyNodes.forEach((node) => {
          this.summaryTotalsObserver.observe(node, {
            childList: true,
            characterData: true,
            subtree: true,
          });
        });
      }

      startRowSubtotalObserver() {
        if (this.rowSubtotalObserver || !window.MutationObserver) return;

        this.rowSubtotalObserver = new MutationObserver(() => {
          if (this.rowSubtotalRefreshQueued) return;

          this.rowSubtotalRefreshQueued = true;
          requestAnimationFrame(() => {
            this.rowSubtotalRefreshQueued = false;
            this.updateState({ refreshRows: false });
          });
        });

        this.rowSubtotalNodes.forEach((node) => {
          this.rowSubtotalObserver.observe(node, {
            childList: true,
            characterData: true,
            subtree: true,
          });
        });
      }

      ensureRowSubtotalMarkup(subtotalNode) {
        let compareNode = subtotalNode.querySelector('[data-row-subtotal-compare]');
        let finalNode = subtotalNode.querySelector('[data-row-subtotal-final]');

        if (!compareNode || !finalNode) {
          subtotalNode.textContent = '';

          compareNode = document.createElement('s');
          compareNode.className = 'gt-pdp__row-subtotal-compare';
          compareNode.setAttribute('data-row-subtotal-compare', '');
          compareNode.hidden = true;

          finalNode = document.createElement('span');
          finalNode.className = 'gt-pdp__row-subtotal-final';
          finalNode.setAttribute('data-row-subtotal-final', '');

          subtotalNode.append(compareNode, finalNode);
        }

        return { compareNode, finalNode };
      }

      updateRowSubtotalNode(subtotalNode, variant, quantity) {
        const { compareNode, finalNode } = this.ensureRowSubtotalMarkup(subtotalNode);
        const unitPrice = this.getEffectiveUnitPrice(variant, quantity);
        const compareUnitPrice = this.getEffectiveCompareUnitPrice(variant, quantity);
        const finalSubtotal = unitPrice * quantity;
        const compareSubtotal = compareUnitPrice * quantity;
        const hasDiscount = quantity > 0 && compareSubtotal > finalSubtotal;

        subtotalNode.dataset.finalSubtotal = `${finalSubtotal}`;
        subtotalNode.dataset.originalSubtotal = `${compareSubtotal}`;
        subtotalNode.classList.toggle('gt-pdp__row-subtotal--sale', hasDiscount);

        compareNode.hidden = !hasDiscount;
        compareNode.textContent = hasDiscount ? this.formatMoney(compareSubtotal) : '';
        finalNode.textContent = this.formatMoney(finalSubtotal);
      }

      updateRowSubtotals() {
        this.rowSubtotalNodes.forEach((subtotalNode) => {
          const variant = this.getVariantById(subtotalNode.dataset.variantId);
          const row = subtotalNode.closest('[data-variant-row]');
          const input = row?.querySelector(`input[data-variant-id="${subtotalNode.dataset.variantId}"]`);
          const quantity = Number(input?.value || 0);
          this.updateRowSubtotalNode(subtotalNode, variant, quantity);
        });
      }

      updateState({ refreshRows = true } = {}) {
        this.refreshInputConstraints();

        if (refreshRows) this.updateRowSubtotals();

        const totals = this.quantityInputs.reduce(
          (acc, input) => {
            const variant = this.getVariantById(input.dataset.variantId);
            const quantity = Number(input.value || 0);
            if (!variant || quantity <= 0) return acc;

            const rowSubtotal = input.closest('[data-variant-row]')?.querySelector('[data-row-subtotal]');
            const renderedSubtotal = Number(rowSubtotal?.dataset.finalSubtotal);
            const visibleSubtotal = Number.isFinite(renderedSubtotal) ? renderedSubtotal : this.parseMoneyToCents(rowSubtotal?.textContent);

            acc.pairs += quantity;
            acc.sizes += 1;
            acc.money += visibleSubtotal !== null && visibleSubtotal >= 0
              ? visibleSubtotal
              : this.getEffectiveUnitPrice(variant, quantity) * quantity;
            return acc;
          },
          { pairs: 0, sizes: 0, money: 0 }
        );

        this.totalPairsNodes.forEach((node) => {
          node.textContent = totals.pairs;
        });
        this.selectedSizesNodes.forEach((node) => {
          node.textContent = totals.sizes;
        });

        this.lastTotalMoneyText = this.formatMoney(totals.money);
        this.isUpdatingSummary = true;
        this.totalMoneyNodes.forEach((node) => {
          node.textContent = this.lastTotalMoneyText;
        });
        this.isUpdatingSummary = false;

        if (this.addButton) {
          this.addButton.disabled = totals.pairs === 0;
          this.addButton.setAttribute('aria-disabled', totals.pairs === 0 ? 'true' : 'false');
        }
      }

      updateActiveVariant(variantId) {
        const variant = this.getVariantById(variantId);
        if (!variant) return;

        this.activeVariantId = variant.id;

        this.querySelectorAll('[data-variant-row]').forEach((row) => {
          row.classList.toggle('is-active', Number(row.dataset.variantId) === Number(variant.id));
        });

        if (this.skuNode) {
          this.skuNode.textContent = variant.sku ? `${this.skuNode.dataset.label || 'SKU:'} ${variant.sku}` : '';
        }

        if (this.priceNode) {
        
          const comparePrice = Number(variant.compare_at_price || 0);
          const activePrice = Number(variant.price || 0);
          const discountPercent = comparePrice > activePrice
            ? Math.round(((comparePrice - activePrice) / comparePrice) * 100)
            : 0;
          const priceHtml = comparePrice > activePrice
            ? `<div class="price price--large price--on-sale"><div class="price__container"><div class="price__sale"><span class="visually-hidden">Precio regular</span><span><s class="price-item price-item--regular">${variant.formatted_compare_at_price}</s></span><span class="visually-hidden">Precio de oferta</span><span class="price-item price-item--sale price-item--last">${variant.formatted_price}</span></div></div></div>`
            : `<div class="price price--large"><div class="price__container"><div class="price__regular"><span class="visually-hidden">Precio</span><span class="price-item price-item--regular">${variant.formatted_price}</span></div></div></div>`;
          this.priceNode.innerHTML = priceHtml;
          if (this.discountNode) {
            if (discountPercent > 0) {
              this.discountNode.textContent = `-${discountPercent}%`;
              this.discountNode.classList.remove('hidden');
            } else {
              this.discountNode.textContent = '';
              this.discountNode.classList.add('hidden');
            }
          }

            const wholesaleProduct = document.querySelector('wholesale-product');

          const productHandle = wholesaleProduct
          .getAttribute('data-product-url')
          .split('/products/')[1];

          const variantId = wholesaleProduct.getAttribute('data-active-variant-id');

          const productId = wholesaleProduct.getAttribute('data-product-id');

          document.querySelectorAll('.price-item').forEach((el) => {

          el.setAttribute('data-wpd-product-handle', productHandle);

          el.setAttribute('data-wpd-variant-id', variantId);

          el.setAttribute('data-wpd-product-id', productId);

          // optional
          el.setAttribute('data-wpd-variant-price', variant.price);

          el.setAttribute(
          'data-wpd-variant-compare-at-price',
          variant.compare_at_price || ''
          );

          });


        }

        const mediaGallery = this.querySelector('media-gallery');
        if (mediaGallery && variant.featured_media_id) {
          mediaGallery.setActiveMedia(`${this.dataset.sectionId}-${variant.featured_media_id}`, true);
        }
      }

      setLoading(loading) {
        if (!this.addButton) return;

        this.addButton.classList.toggle('loading', loading);
        this.addButton.setAttribute('aria-disabled', loading || this.addButton.disabled ? 'true' : 'false');
        const spinner = this.addButton.querySelector('.loading__spinner');
        if (spinner) spinner.classList.toggle('hidden', !loading);
      }

      setMessage(message, isError = false) {
        if (!this.messageNode) return;
        this.messageNode.textContent = message || '';
        this.messageNode.classList.toggle('is-error', Boolean(isError));
      }

      onClearAll(preserveMessage = false) {
        this.quantityInputs.forEach((input) => {
          input.value = 0;
        });
        if (!preserveMessage) this.setMessage('');
        this.updateState();
      }

      buildItemsPayload() {
        return this.quantityInputs
          .map((input) => {
            const variant = this.getVariantById(input.dataset.variantId);
            if (!variant) return null;

            const quantity = this.sanitizeQuantity(input, variant, 'down');
            input.value = quantity;

            if (quantity <= 0) return null;

            return {
              id: Number(variant.id),
              quantity,
              properties: {
                _source: 'wholesale_pdp',
              },
            };
          })
          .filter(Boolean);
      }

      getInventoryValidationMessage(items) {
        for (const item of items) {
          const variant = this.getVariantById(item.id);
          if (!variant) continue;

          const constraints = this.getVariantConstraints(variant);
          if (constraints.maximum !== null && item.quantity > constraints.maximum) {
            const label = variant.display_title || variant.title || 'esta variante';
            if (constraints.maximum <= 0) {
              return `${label} ya no tiene inventario disponible en este momento.`;
            }

            return `${label} solo permite ${constraints.maximum} pares disponibles ahora.`;
          }
        }

        return '';
      }

      async onAddAll() {
        await this.syncCartState({ silent: true });
        this.refreshInputConstraints();

        const items = this.buildItemsPayload();

        if (!items.length) {
          this.setMessage(this.dataset.selectionErrorMessage || 'Revisa las cantidades seleccionadas antes de agregar.', true);
          this.updateState();
          return;
        }

        if (this.addButton?.getAttribute('aria-disabled') === 'true') return;

        const validationMessage = this.getInventoryValidationMessage(items);
        if (validationMessage) {
          this.setMessage(validationMessage, true);
          this.updateState();
          return;
        }

        this.setLoading(true);
        this.setMessage('');

        try {
          const config = fetchConfig('javascript');
          const payload = {
            items,
          };

          if (this.cart) {
            payload.sections = this.cart.getSectionsToRender().map((section) => section.id || section.section);
            payload.sections_url = window.location.pathname;
            this.cart.setActiveElement?.(document.activeElement);
          }

          config.body = JSON.stringify(payload);

          const response = await fetch(`${routes.cart_add_url}`, config);
          const parsedResponse = await response.json();

          if (!response.ok || parsedResponse.status) {
            await this.syncCartState({ silent: true });
            this.refreshInputConstraints();
            this.updateState();
            this.setMessage(parsedResponse.description || parsedResponse.message || this.dataset.errorMessage || 'No se pudo agregar al carrito.', true);
            return;
          }

          window.location.href = routes.cart_url;
          return;
        } catch (error) {
          console.error(error);
          await this.syncCartState({ silent: true });
          this.refreshInputConstraints();
          this.updateState();
          this.setMessage(this.dataset.errorMessage || 'Ocurrio un error al agregar el pedido.', true);
        } finally {
          this.setLoading(false);
        }
      }
    }
  );
}
